import type { JSX } from 'react';
import { Stack, Typography } from '@mui/material';
import { DataGridProProps, GridColDef } from '@mui/x-data-grid-pro';

import { AllocatedLineItemMapping } from 'common/src/allocations/allocatedLineItemMappings';
import { IMeter } from 'common/src/types/interfaces';

import DataTable from 'portal/src/components/common/DataTable';
import addActionColumnProps from 'portal/src/components/common/tables/addActionColumnProps';
import addDateColumnProps from 'portal/src/components/common/tables/addDateColumnProps';
import { numberFormatter } from 'portal/src/utils/numberFormatter';

import { AllocationLineItemRow } from './AllocationLineItemRow';
import allocationTableStyle from './allocationTableStyle';

import { useBillFormContext } from '../BillFormContext';

const MasterLineItemValueCell = ({
  value,
  valueDifference,
  formatOptions,
}: {
  value: string;
  valueDifference: number;
  formatOptions?: Intl.NumberFormatOptions;
}): JSX.Element => {
  return (
    <Stack
      direction="column"
      sx={{ height: '100%', justifyContent: 'space-around' }}
    >
      <Typography component="span" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
      {valueDifference !== 0 && (
        <Typography component="span">
          {'('}
          <Typography component="span" color="error">
            {numberFormatter(valueDifference, formatOptions)}
          </Typography>
          {')'}
        </Typography>
      )}
    </Stack>
  );
};

const AllocationLineItems = ({
  disabled = false,
  editable = false,
}: {
  disabled?: boolean;
  editable?: boolean;
}) => {
  const {
    allocatedLineItemMappings,
    allocationMeters,
    bill,
    lineItemSubtypes,
    processAllocationLineItemEdit,
  } = useBillFormContext();

  function getAllocationMapping(
    masterLineItemId: string,
  ): AllocatedLineItemMapping | undefined {
    if (!masterLineItemId) {
      return undefined;
    }

    return allocatedLineItemMappings.data.find(
      (m) => m.masterLineItem.id === masterLineItemId,
    );
  }

  const columns: GridColDef[] = [
    {
      field: 'meter',
      headerName: 'Meter',
      flex: 1,
      valueGetter: (value: IMeter) => value?.name,
    },
    {
      field: 'facility',
      headerName: 'Facility Name',
      flex: 1,
      valueGetter: (_, row) => row?.meter?.parentFacility?.name,
    },
    {
      field: 'startDate',
      ...addDateColumnProps('Start Date'),
    },
    {
      field: 'endDate',
      ...addDateColumnProps('End Date'),
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.2,
    },
    {
      field: 'subTypeId',
      headerName: 'Sub Type',
      flex: 0.5,
      valueFormatter: (value: string) =>
        lineItemSubtypes.data.find(({ id }) => id === value)?.name ?? '',
    },
    {
      field: 'unitOfMeasure',
      headerName: 'Unit',
      width: 96,
    },
    {
      editable,
      field: 'usage',
      headerName: 'Usage',
      type: 'number',
      width: 96,
      renderCell: ({ row }: { row: AllocationLineItemRow }) => {
        const formatOptions: Intl.NumberFormatOptions = { style: 'decimal' };
        const usage = numberFormatter(row.usage, formatOptions);

        if (row.isMaster) {
          const usageDifference =
            getAllocationMapping(row.id)?.usageDifference ?? 0;
          return (
            <MasterLineItemValueCell
              value={usage}
              valueDifference={usageDifference}
              formatOptions={formatOptions}
            />
          );
        }
        return usage;
      },
    },
    {
      editable,
      field: 'cost',
      headerName: 'Cost',
      flex: 0.5,
      type: 'number',
      renderCell: ({ row }: { row: AllocationLineItemRow }) => {
        const cost = numberFormatter(row.cost);

        if (row.isMaster) {
          const costDifference =
            getAllocationMapping(row.id)?.costDifference ?? 0;
          return (
            <MasterLineItemValueCell
              value={cost}
              valueDifference={costDifference}
            />
          );
        }
        return cost;
      },
    },
  ];

  const getTreeDataPath: DataGridProProps['getTreeDataPath'] = (
    row: AllocationLineItemRow,
  ): string[] => row.path;

  const getGridRows = (): AllocationLineItemRow[] => {
    const gridRows: AllocationLineItemRow[] =
      allocatedLineItemMappings.data.reduce((rows, m) => {
        const masterId = m.masterLineItem.id;

        rows.push({
          isMaster: true,
          path: [masterId],
          ...m.masterLineItem,
        });

        rows.push(
          ...m.allocatedLineItems.map((li) => {
            return {
              isMaster: false,
              path: [masterId, li.id],
              ...li,
            };
          }),
        );

        return rows;
      }, []);

    return gridRows;
  };

  const isCellEditable = ({ row }: { row: AllocationLineItemRow }): boolean => {
    return !row.isMaster;
  };

  return (
    <DataTable
      actionsConfig={
        bill.id && bill.isAllocated
          ? {
              inRowEditConfig: {
                processRowUpdate: processAllocationLineItemEdit,
                btnConfig: (row) => ({
                  isEditDisabled: !editable || disabled || row.isMaster,
                }),
              },
            }
          : undefined
      }
      autoHeight
      checkboxSelection={false}
      columns={columns}
      dataFetchError={
        !disabled &&
        (allocatedLineItemMappings.isError || allocationMeters.isError)
      }
      defaultGroupingExpansionDepth={-1}
      getRowClassName={({ row }) =>
        row.isMaster ? 'striped-even' : 'striped-odd'
      }
      getTreeDataPath={getTreeDataPath}
      groupingColDef={{
        headerName: 'Account Number',
        valueGetter: (_, row) => {
          return row.meter?.parentAccount?.number;
        },
      }}
      hideFooter
      isCellEditable={isCellEditable}
      isRowNavigation={false}
      loading={
        !disabled &&
        (allocatedLineItemMappings.isFetching || allocationMeters.isFetching)
      }
      rows={getGridRows()}
      sx={allocationTableStyle}
      treeData
    />
  );
};

export default AllocationLineItems;
